import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FiAlertTriangle, FiBell, FiBookOpen, FiBookmark, FiBriefcase, FiCalendar,
  FiCheckSquare, FiClipboard, FiClock, FiColumns, FiEdit3, FiFileText,
  FiFilePlus, FiFolder, FiGitBranch, FiGrid, FiLayers, FiLink2,
  FiMessageCircle, FiMessageSquare, FiPenTool, FiSearch, FiShuffle, FiTable,
  FiUnlock, FiUserPlus, FiUsers, FiVideo,
} from 'react-icons/fi';
import styles from './Home.module.css';
import SEO from '../components/SEO';
import showcaseImg from '../assets/images/showcase.png';
import heroBgImg from '../assets/images/hero-bg.webp';
import { HOME } from '../config/content';
import { useAuth } from '../store/AuthContext';
import AuthFlow from '../components/auth/AuthFlow';

/* ------------------------------------------------------------------ */
/*  Platform row icons                                                */
/*  content.ts carries icon *keys* so it stays free of JSX; this is    */
/*  the only place the key is resolved to a component.                 */
/* ------------------------------------------------------------------ */
const PILLAR_ICONS: Record<string, React.ReactNode> = {
  // Case management
  userPlus: <FiUserPlus />,
  briefcase: <FiBriefcase />,
  folder: <FiFolder />,
  invoice: <FiFileText />,
  clock: <FiClock />,
  shuffle: <FiShuffle />,
  calendar: <FiCalendar />,
  signature: <FiEdit3 />,
  video: <FiVideo />,
  // Workflow automation
  checkSquare: <FiCheckSquare />,
  alert: <FiAlertTriangle />,
  layers: <FiLayers />,
  columns: <FiColumns />,
  bell: <FiBell />,
  penTool: <FiPenTool />,
  grid: <FiGrid />,
  bookmark: <FiBookmark />,
  form: <FiFilePlus />,
  counsel: <FiMessageCircle />,
  table: <FiTable />,
  // Legal research
  search: <FiSearch />,
  unlock: <FiUnlock />,
  bookOpen: <FiBookOpen />,
  gitBranch: <FiGitBranch />,
  users: <FiUsers />,
  clipboard: <FiClipboard />,
  link: <FiLink2 />,
  message: <FiMessageSquare />,
};

/* ------------------------------------------------------------------ */
/*  Reusable reveal wrapper (replaces .reveal + IntersectionObserver) */
/* ------------------------------------------------------------------ */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className, id }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  HOME COMPONENT                                                    */
/* ------------------------------------------------------------------ */
const Home: React.FC = () => {
  const { authIntent } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);

  // When the navbar (or another page) asks to open the auth card, bring it into view.
  useEffect(() => {
    if (authIntent) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [authIntent]);

  return (
    <main className={styles.page}>
      <SEO path="/" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ====== HERO ====== */}
      <div className={styles.heroWrapper}>
        <div className={styles.heroBg} style={{ backgroundImage: `url(${heroBgImg})` }} />
        <div className={styles.hero}>
          <motion.div
            className={styles.heroLeft}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.heroBadge}>
              <div className={styles.badgeDot} />
              <span>{HOME.hero.badge}</span>
            </div>
            <h1>
              {HOME.hero.title.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}<em>{HOME.hero.titleAccent}</em>
            </h1>
            <p className={styles.heroSub}>
              {HOME.hero.subtitle}
            </p>
            <div className={styles.heroActions}>
              <a
                href={HOME.hero.ctaPrimaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btnLarge}
              >
                {HOME.hero.ctaPrimary}
              </a>
              {/* There is no self-serve demo reel yet, so this books one. */}
              <a href={HOME.hero.ctaSecondaryLink} className={styles.btnText}>
                {HOME.hero.ctaSecondary}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M6.5 5.5L10.5 8L6.5 10.5V5.5Z" fill="currentColor" />
                </svg>
              </a>
            </div>
            <div className={styles.heroPills}>
              {HOME.hero.pills.map((pill, i) => (
                <div key={i} className={styles.heroPill}>
                  <span>{pill.icon}</span> {pill.text}
                </div>
              ))}
            </div>
            <div className={styles.heroStats}>
              {HOME.hero.stats.map((stat, i) => (
                <div key={i} className={styles.statItem}>
                  <span className={styles.statNum}>{stat.value}</span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* LOGIN CARD — hosts the full inline auth flow (no popups) */}
          <motion.div
            ref={cardRef}
            className={styles.loginCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            <div className={styles.loginCardInner}>
              <AuthFlow defaultMode="signup" />
            </div>
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ====== PLATFORM — three module rows ====== */}
      <section id="platform" className={styles.section}>
        <Reveal className={styles.featuresHeader}>
          <div>
            <div className={styles.sectionTag}>{HOME.pillars.tag}</div>
            <h2 className={styles.sectionTitle}>
              {HOME.pillars.title}<br /><em>{HOME.pillars.titleAccent}</em>
            </h2>
          </div>
          <p className={styles.sectionSub}>
            {HOME.pillars.subtitle}
          </p>
        </Reveal>

        <div className={styles.pillarRows}>
          {HOME.pillars.rows.map((row) => (
            <Reveal key={row.name} className={styles.pillarRow}>
              <div className={styles.pillarLead}>
                <span className={styles.pillarIndex}>{row.index}</span>
                <h3 className={styles.pillarName}>{row.name}</h3>
                <p className={styles.pillarTagline}>{row.tagline}</p>
                <p className={styles.pillarDesc}>{row.desc}</p>
                {row.link.startsWith('http') ? (
                  <a
                    href={row.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.pillarLink}
                  >
                    {row.linkText}
                  </a>
                ) : (
                  <a href={row.link} className={styles.pillarLink}>
                    {row.linkText}
                  </a>
                )}
              </div>
              <div className={styles.pillarItems}>
                {row.items.map((item) => (
                  <div key={item.label} className={styles.pillarItem}>
                    <span className={styles.pillarItemIcon} aria-hidden="true">
                      {PILLAR_ICONS[item.icon]}
                    </span>
                    <span className={styles.pillarItemLabel}>{item.label}</span>
                    <span className={styles.pillarItemDesc}>{item.desc}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ════════════════ PRACTICE-AREA SUITES ════════════════ */}
      <section id="suites" className={styles.section}>
        <Reveal className={styles.featuresHeader}>
          <div>
            <div className={styles.sectionTag}>{HOME.suites.tag}</div>
            <h2 className={styles.sectionTitle}>
              {HOME.suites.title}<br /><em>{HOME.suites.titleAccent}</em>
            </h2>
          </div>
          <p className={styles.sectionSub}>{HOME.suites.subtitle}</p>
        </Reveal>

        <div className={styles.suitesGrid}>
          {HOME.suites.items.map((suite) => (
            <Reveal key={suite.id} className={styles.suiteCard}>
              <div className={styles.suiteHead}>
                <h3 className={styles.suiteName}>{suite.name}</h3>
                <span className={styles.suiteCount}>{suite.count}</span>
              </div>
              <p className={styles.suiteDesc}>{suite.desc}</p>
              <ul className={styles.suiteList}>
                {suite.services.map((svc) => (
                  <li key={svc}>{svc}</li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        <div className={styles.suiteTools}>
          {HOME.suites.tools.map((tool) => (
            <Reveal key={tool.name} className={styles.suiteTool}>
              <span className={styles.pillarItemIcon} aria-hidden="true">
                {PILLAR_ICONS[tool.icon]}
              </span>
              <div>
                <h4 className={styles.suiteToolName}>
                  {tool.name}
                  {tool.badge && <span className={styles.suiteBadge}>{tool.badge}</span>}
                </h4>
                <p className={styles.suiteToolDesc}>{tool.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className={styles.suitesFooter}>
          <a href={HOME.suites.link} className={styles.pillarLink}>
            {HOME.suites.linkText}
          </a>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ====== PRODUCT SHOWCASE ====== */}
      <Reveal className={styles.showcaseSection}>
        <div className={styles.showcaseGlow} />
        <div className={styles.showcaseInner}>
          <div className={styles.showcaseLeft}>
            <div className={styles.sectionTag}>{HOME.showcase.tag}</div>
            <h2 className={styles.sectionTitle}>
              {HOME.showcase.title}<em>{HOME.showcase.titleAccent}</em>{HOME.showcase.titleSuffix.split('\n').map((line, i) => <React.Fragment key={i}><br />{line}</React.Fragment>)}
            </h2>
            <p className={styles.sectionSub}>
              {HOME.showcase.subtitle}
            </p>
            <ul className={styles.showcaseChecklist}>
              {HOME.showcase.checklist.map((item, i) => (
                <li key={i}>
                  <span className={styles.checkIcon}>{'\u2713'}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className={styles.showcaseActions}>
              <a
                href={HOME.showcase.ctaPrimaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btnLarge}
              >
                {HOME.showcase.ctaPrimary}
              </a>
              <a href={HOME.showcase.ctaSecondaryLink} className={styles.btnGhost}>
                {HOME.showcase.ctaSecondary}
              </a>
            </div>
          </div>
          <div className={styles.showcaseRight}>
            <div className={styles.showcaseImgWrap}>
              <img
                src={showcaseImg}
                alt={HOME.showcase.imgAlt}
                className={styles.showcaseImg}
              />
            </div>
            <div className={styles.showcaseBadge}>
              <span className={styles.showcaseBadgeIcon}>{HOME.showcase.badgeIcon}</span>
              <div className={styles.showcaseBadgeText}>
                <span className={styles.showcaseBadgeLabel}>{HOME.showcase.badgeLabel}</span>
                <span className={styles.showcaseBadgeValue}>{HOME.showcase.badgeValue}</span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ====== PRODUCTS ====== */}
      <section id="products" className={styles.section}>
        <Reveal>
          <div className={styles.sectionTag}>{HOME.products.tag}</div>
          <h2 className={styles.sectionTitle}>
            {HOME.products.title}<em>{HOME.products.titleAccent}</em>
          </h2>
          <p className={styles.sectionSub}>
            {HOME.products.subtitle}
          </p>
        </Reveal>
        <Reveal className={styles.productsGrid}>
          {HOME.products.items.map((p) => (
            <div
              key={p.name}
              className={`${styles.productCard} ${p.featured ? styles.productCardFeatured : ''}`}
            >
              <div className={styles.productLabel}>{p.label}</div>
              <h3>{p.name}</h3>
              <p>{p.desc}</p>
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.productLink}
              >
                {p.linkText}
              </a>
            </div>
          ))}
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ====== RESOURCES ====== */}
      <section id="resources" className={styles.section}>
        <Reveal>
          <div className={styles.sectionTag}>{HOME.resources.tag}</div>
          <h2 className={styles.sectionTitle}>
            {HOME.resources.title.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}<em>{HOME.resources.titleAccent}</em>
          </h2>
          <p className={styles.sectionSub}>
            {HOME.resources.subtitle}
          </p>
        </Reveal>
        <Reveal className={styles.resourcesGrid}>
          {HOME.resources.items.map((r) => (
            <a key={r.title} href={r.link} className={styles.resourceItem}>
              <div className={styles.resourceIconWrap}>{r.icon}</div>
              <div className={styles.resourceContent}>
                <h4>{r.title}</h4>
                <p>{r.desc}</p>
                <span className={styles.resourceTag}>{r.tag}</span>
              </div>
            </a>
          ))}
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ====== BLOGS ====== */}
      <section id="blogs" className={styles.section}>
        <Reveal className={styles.blogsHeader}>
          <div>
            <div className={styles.sectionTag}>{HOME.blogs.tag}</div>
            <h2 className={styles.sectionTitle}>
              {HOME.blogs.title}<em>{HOME.blogs.titleAccent}</em>{HOME.blogs.titleSuffix}
            </h2>
          </div>
          <a href={HOME.blogs.viewAllLink} className={styles.btnGhost}>
            {HOME.blogs.viewAllText}
          </a>
        </Reveal>
        <Reveal className={styles.blogsGrid}>
          {HOME.blogs.items.map((b) => (
            <a
              key={b.title}
              href={`/blogs/${b.slug}`}
              className={`${styles.blogCard} ${b.featured ? styles.blogCardFeatured : ''}`}
            >
              <div className={styles.blogContent}>
                <div className={styles.blogMeta}>
                  <span className={styles.blogCategory}>{b.category}</span>
                  <div className={styles.blogDot} />
                  <span className={styles.blogDate}>{b.date}</span>
                </div>
                <h4>{b.title}</h4>
                <p>{b.desc}</p>
                <div className={styles.blogReadMore}>{HOME.blogs.readMore}</div>
              </div>
            </a>
          ))}
        </Reveal>
      </section>

      {/* ====== CTA BANNER ====== */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>
              {HOME.cta.title.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}<em>{HOME.cta.titleAccent}</em>{HOME.cta.titleSuffix}
            </h2>
            <p>
              {HOME.cta.subtitle}
            </p>
          </div>
          <div className={styles.ctaActions}>
            <a
              href={HOME.cta.ctaPrimaryLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnLarge}
              style={{ textAlign: 'center' }}
            >
              {HOME.cta.ctaPrimary}
            </a>
            <a href={HOME.cta.ctaSecondaryLink} className={styles.btnGhost} style={{ textAlign: 'center' }}>
              {HOME.cta.ctaSecondary}
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default Home;
